/**--external-- */
import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Checkbox } from "@toddle-edu/ds-web";

/**--internal-- */
import { colors } from "Constants";
import {
  getHTMLLabel,
  getLineHeightFromDepth,
} from "GenericTableEditor/GenericTableEditorUtils";
import { SINGLE_LEVEL } from "Fields/FieldUtils";
import { multiLineEllipsis } from "Utils";

/**--relative-- */
import classes from "./SelectableLabelCell.scss";

const MultiLevelLabelCell = props => {
  const {
    label,
    depth,
    searchText,
    isSelected,
    showCheckbox,
    truncateText,
    isLabelEmpty,
    onClickCheckbox,
    disableCheckbox,
  } = props;

  const textContainerClasses = classNames(classes.textContainer, {
    ["heading-5"]: depth == 1,
    ["heading-6"]: depth == 2,
    ["text-label-3"]: depth == 3,
    [`text-body-3`]: depth == 4,
    [`text-body-3 ${classes.depth5}`]: depth == 5,
    [classes.emptyLabel]: isLabelEmpty,
  });

  const containerStyle = {
    paddingLeft: `${(depth - 1) * 30 + (showCheckbox ? 0 : 30)}px`,
  };

  const textContainerStyle = {
    ...(truncateText
      ? multiLineEllipsis({
          lineHeight: `${getLineHeightFromDepth(depth)}rem`,
          lineCount: 2,
        })
      : {}),
  };

  const htmlLabel = getHTMLLabel({
    label,
    searchText,
    style: {
      color: colors.blue400,
    },
  });

  const containerClasses = classNames(classes.container, {
    [classes.selectableContainer]: showCheckbox,
  });

  return (
    <div
      className={containerClasses}
      style={containerStyle}
      onClick={showCheckbox ? onClickCheckbox : _.noop}
    >
      {showCheckbox ? (
        <Checkbox
          size="xx-small"
          isChecked={isSelected}
          disabled={disableCheckbox}
        />
      ) : null}

      <div
        className={textContainerClasses}
        style={textContainerStyle}
        dangerouslySetInnerHTML={{ __html: htmlLabel }}
      />
    </div>
  );
};

const SingleLevelLabelCell = props => {
  const {
    label,
    searchText,
    isSelected,
    showCheckbox,
    onClickCheckbox,
    disableCheckbox,
  } = props;

  const htmlLabel = getHTMLLabel({
    label,
    searchText,
    style: {
      color: colors.blue400,
    },
  });

  const containerClasses = classNames(classes.container, {
    [classes.selectableContainer]: showCheckbox,
  });

  const textContainerClasses = classNames(classes.textContainer, "text-body-1");

  return (
    <div
      className={containerClasses}
      onClick={showCheckbox ? onClickCheckbox : _.noop}
    >
      {showCheckbox ? (
        <Checkbox
          size="xx-small"
          isChecked={isSelected}
          disabled={disableCheckbox}
        />
      ) : null}

      <div
        className={textContainerClasses}
        dangerouslySetInnerHTML={{ __html: htmlLabel }}
      />
    </div>
  );
};

const SelectableLabelCell = props => {
  const {
    label,
    variant,
    depth,
    searchText,
    isSelected,
    showCheckbox,
    isLabelEmpty,
    disableCheckbox,
    onClickCheckbox,
    truncateText,
  } = props;

  return variant === SINGLE_LEVEL ? (
    <SingleLevelLabelCell
      label={label}
      searchText={searchText}
      isSelected={isSelected}
      showCheckbox={showCheckbox}
      isLabelEmpty={isLabelEmpty}
      disableCheckbox={disableCheckbox}
      onClickCheckbox={onClickCheckbox}
    />
  ) : (
    <MultiLevelLabelCell
      label={label}
      depth={depth}
      searchText={searchText}
      isSelected={isSelected}
      showCheckbox={showCheckbox}
      isLabelEmpty={isLabelEmpty}
      disableCheckbox={disableCheckbox}
      onClickCheckbox={onClickCheckbox}
      truncateText={truncateText}
    />
  );
};

export default SelectableLabelCell;

SelectableLabelCell.defaultProps = {
  label: "",
  variant: SINGLE_LEVEL,
  searchText: "",
  isSelected: false,
  showCheckbox: false,
};

SelectableLabelCell.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.string,
  depth: PropTypes.number,
  searchText: PropTypes.string,
  isSelected: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  onClickCheckbox: PropTypes.func,
};
